import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, HelpOutline } from '@mui/icons-material';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';
import { glassStyles } from '../../tokens/designTokens';
import { font } from '../../utils/fontHelper';
import HomeCardStack from '../widgets/HomeCardStack';

interface OpeningCreditsSheetProps {
  open: boolean;
  onClose: () => void;
}

const SUBTITLE = "Getting started with Watchlist.";

const Content: React.FC<{ onClose: () => void; useGrid?: boolean }> = ({ onClose, useGrid = false }) => {
  const theme = useTheme();
  return (
    // display:flex + overflow:hidden + minHeight:0 lets the inner scroll box expand
    // correctly inside both the BottomSheet (maxHeight) and Dialog (maxHeight).
    <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, flex: 1 }}>
      {/* Header — fixed */}
      <BottomSheetDraggableHeader sx={{ display: 'flex', alignItems: 'center', px: 2.5, pt: 2.5, pb: 1.25, gap: 1, flexShrink: 0 }}>
        <HelpOutline sx={{ fontSize: 22, color: theme.palette.text.secondary }} />
        <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 18, color: theme.palette.text.primary, flex: 1 }}>
          Help
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: theme.palette.text.secondary }}>
          <Close fontSize="small" />
        </IconButton>
      </BottomSheetDraggableHeader>

      <Typography sx={{ fontFamily: font(), fontSize: 13, color: theme.palette.text.secondary, lineHeight: 1.5, px: 2.5, pb: 2, flexShrink: 0 }}>
        {SUBTITLE}
      </Typography>

      {/* Scrollable card list */}
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, px: 2.5, pb: 3 }}>
        <HomeCardStack alwaysShow forceGrid={useGrid} forceList={!useGrid} hideDismiss />
      </Box>
    </Box>
  );
};

const OpeningCreditsSheet: React.FC<OpeningCreditsSheetProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!isMobile) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...glassStyles.popup(isDark),
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <DialogContent sx={{ p: 0, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Content onClose={onClose} useGrid />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <BottomSheet open={open} onClose={onClose} maxHeight="60dvh">
      <Content onClose={onClose} />
    </BottomSheet>
  );
};

export default OpeningCreditsSheet;
